import React from "react";
import moment from "moment";
import { styled } from "@material-ui/styles";
import { injectIntl } from "react-intl";
import Link from "../link";
import { ITeam } from "../../types";
import { IStyledArguments } from "../../types/styled-arguments";
import { getNextTraining } from "./nextTraining";
import messages from "./PageDashboard.intl";

const Card = styled(Link)(({ theme }: IStyledArguments) => ({
  display: "block",
  minWidth: 200,
  flexShrink: 0,
  background: "#ffffff",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 16,
  padding: theme.spacing(2),
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  textDecoration: "none",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
}));

const Crest = styled("div")(({ theme }: IStyledArguments) => ({
  width: 44,
  height: 44,
  borderRadius: 12,
  background: theme.palette.extra.accentSoft,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "0.85rem",
  color: theme.palette.primary.dark,
}));

const Name = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "0.95rem",
  color: theme.palette.text.primary,
  marginTop: theme.spacing(1.5),
}));

const ChipsRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(0.75),
  flexWrap: "wrap",
  marginTop: theme.spacing(1.25),
}));

const Chip = styled("span")<{ $accent?: boolean }>(
  ({ theme, $accent }: IStyledArguments & { $accent?: boolean }) => ({
    background: $accent ? theme.palette.extra.accentSoft : theme.palette.extra.backgroundGrey,
    color: $accent ? theme.palette.primary.dark : theme.palette.text.secondary,
    borderRadius: 8,
    padding: theme.spacing(0.35, 1),
    fontSize: "0.66rem",
    fontWeight: 600,
  })
);

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

interface IDashboardTeamCard {
  team: ITeam;
}

const DashboardTeamCard = injectIntl<IDashboardTeamCard>(({ team, intl }) => {
  const nextTraining = getNextTraining(team.trainingSetup);

  return (
    <Card to={`/teams/${team.id}`}>
      <Crest>{getInitials(team.name)}</Crest>
      <Name>{team.name}</Name>
      <ChipsRow>
        <Chip>
          {(team.squad && team.squad.length) || 0}{" "}
          {intl.formatMessage(messages.playersChip, { n: (team.squad && team.squad.length) || 0 })}
        </Chip>
        {nextTraining ? (
          <Chip $accent>
            {moment(nextTraining.date).format("ddd")} {nextTraining.time}
          </Chip>
        ) : (
          <Chip>{intl.formatMessage(messages.noTrainingChip)}</Chip>
        )}
      </ChipsRow>
    </Card>
  );
});

export default DashboardTeamCard;
